import exifr from 'exifr';

export interface PhotoMetadata {
  takenAt: Date | null;
  gps: { lat: number; lng: number } | null;
  hash: string;
  sharpness: number;
  exposure: number;
  thumbDataUrl: string;
}

const THUMB_SIZE = 96;
const HASH_W = 9;
const HASH_H = 8;

/** Neutral "preview unavailable" placeholder — indigo-tinted, matches the app's palette. */
export const UNSUPPORTED_PREVIEW_DATA_URL = 'data:image/svg+xml;utf8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96">' +
  '<rect width="96" height="96" fill="#EEF2FF"/>' +
  '<rect x="24" y="30" width="48" height="36" rx="4" fill="none" stroke="#A5B4FC" stroke-width="3"/>' +
  '<circle cx="36" cy="42" r="4" fill="#A5B4FC"/>' +
  '<path d="M24 58l14-12 12 10 10-8 12 12" fill="none" stroke="#A5B4FC" stroke-width="3"/>' +
  '</svg>'
);

/**
 * Stable, file-specific (not content-based) fallback hash for photos the
 * browser can't decode into a canvas (see extractPhotoMetadata below) — just
 * needs to be consistent per file and distinct across different files so
 * undecodable photos don't get falsely burst-collapsed with one another.
 */
function fallbackHash(file: File): string {
  const input = `${file.name}|${file.size}|${file.lastModified}`;
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < input.length; i++) {
    const ch = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = (Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909)) >>> 0;
  h2 = (Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909)) >>> 0;
  return (h1.toString(16).padStart(8, '0') + h2.toString(16).padStart(8, '0')).slice(0, 16);
}

async function readTakenAt(file: File): Promise<Date | null> {
  try {
    const meta = await exifr.parse(file, ['DateTimeOriginal']);
    return meta?.DateTimeOriginal ? new Date(meta.DateTimeOriginal) : null;
  } catch {
    return null;
  }
}

async function readGps(file: File): Promise<{ lat: number; lng: number } | null> {
  try {
    const gps = await exifr.gps(file);
    if (gps?.latitude != null && gps?.longitude != null) {
      return { lat: gps.latitude, lng: gps.longitude };
    }
    return null;
  } catch {
    return null;
  }
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (err) => { URL.revokeObjectURL(url); reject(err); };
    img.src = url;
  });
}

function toGrayscale(data: Uint8ClampedArray, w: number, h: number): Float32Array {
  const out = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) {
    out[i] = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
  }
  return out;
}

function resampleNearest(gray: Float32Array, w: number, h: number, tw: number, th: number): Float32Array {
  const out = new Float32Array(tw * th);
  for (let y = 0; y < th; y++) {
    const sy = Math.min(h - 1, Math.floor((y / th) * h));
    for (let x = 0; x < tw; x++) {
      const sx = Math.min(w - 1, Math.floor((x / tw) * w));
      out[y * tw + x] = gray[sy * w + sx];
    }
  }
  return out;
}

/**
 * Difference hash (dHash): downsample to a 9x8 grid and record, for each row,
 * whether each pixel is brighter than its right neighbor — 8 rows * 8
 * comparisons = 64 bits, encoded as a 16-char hex string. Robust to minor
 * recompression/resizing, which is exactly what near-duplicate burst shots
 * look like.
 */
export function computeDHashFromGray(gray: Float32Array, w: number, h: number): string {
  const small = resampleNearest(gray, w, h, HASH_W, HASH_H);
  let bits = '';
  for (let y = 0; y < HASH_H; y++) {
    for (let x = 0; x < HASH_W - 1; x++) {
      bits += small[y * HASH_W + x] > small[y * HASH_W + x + 1] ? '1' : '0';
    }
  }
  let hex = '';
  for (let i = 0; i < bits.length; i += 4) {
    hex += parseInt(bits.slice(i, i + 4), 2).toString(16);
  }
  return hex;
}

/**
 * Laplacian-variance-style sharpness over a downsampled grid: high variance
 * in the edge response means lots of fine detail (sharp); low variance means
 * a flat/blurry image. Normalized to a rough 0-1 scale — the divisor is a
 * starting point, meant to be tuned during the real-photo-library calibration
 * pass (see project plan), not treated as exact.
 */
export function computeSharpnessFromGray(gray: Float32Array, w: number, h: number): number {
  const size = 32;
  const small = resampleNearest(gray, w, h, size, size);
  let sum = 0;
  let sumSq = 0;
  let count = 0;
  for (let y = 1; y < size - 1; y++) {
    for (let x = 1; x < size - 1; x++) {
      const center = small[y * size + x];
      const laplacian =
        4 * center -
        small[(y - 1) * size + x] -
        small[(y + 1) * size + x] -
        small[y * size + (x - 1)] -
        small[y * size + (x + 1)];
      sum += laplacian;
      sumSq += laplacian * laplacian;
      count++;
    }
  }
  const mean = sum / count;
  const variance = sumSq / count - mean * mean;
  return Math.max(0, Math.min(1, variance / 2000));
}

/**
 * Exposure/quality score: penalizes near-black, near-white, or near-flat
 * (low-contrast) images — the signature of accidental pocket shots, blank
 * walls, or plain-color screenshots — without any ML.
 */
export function computeExposureFromGray(gray: Float32Array): number {
  let sum = 0;
  let sumSq = 0;
  for (let i = 0; i < gray.length; i++) {
    sum += gray[i];
    sumSq += gray[i] * gray[i];
  }
  const mean = sum / gray.length;
  const variance = Math.max(0, sumSq / gray.length - mean * mean);
  const stdDev = Math.sqrt(variance);

  const brightnessScore = 1 - Math.abs(mean - 128) / 128;
  const contrastScore = Math.min(1, stdDev / 60);
  return Math.max(0, Math.min(1, 0.5 * brightnessScore + 0.5 * contrastScore));
}

/** Hamming distance between two same-length hex-encoded bit strings. */
export function hammingDistance(hashA: string, hashB: string): number {
  const len = Math.max(hashA.length, hashB.length);
  let dist = 0;
  for (let i = 0; i < len; i++) {
    let x = parseInt(hashA[i] ?? '0', 16) ^ parseInt(hashB[i] ?? '0', 16);
    while (x) { dist += x & 1; x >>= 1; }
  }
  return dist;
}

/** Combined cheap (no-ML) quality score used to rank/cull photos. */
export function cheapQuality(sharpness: number, exposure: number): number {
  return 0.6 * sharpness + 0.4 * exposure;
}

function analyzeCanvas(img: HTMLImageElement): { thumbDataUrl: string; hash: string; sharpness: number; exposure: number } {
  const canvas = document.createElement('canvas');
  canvas.width = THUMB_SIZE;
  canvas.height = THUMB_SIZE;
  const ctx = canvas.getContext('2d')!;

  const side = Math.min(img.naturalWidth, img.naturalHeight) || THUMB_SIZE;
  const sx = Math.max(0, (img.naturalWidth - side) / 2);
  const sy = Math.max(0, (img.naturalHeight - side) / 2);
  ctx.drawImage(img, sx, sy, side, side, 0, 0, THUMB_SIZE, THUMB_SIZE);

  const thumbDataUrl = canvas.toDataURL('image/jpeg', 0.7);
  const { data } = ctx.getImageData(0, 0, THUMB_SIZE, THUMB_SIZE);
  const gray = toGrayscale(data, THUMB_SIZE, THUMB_SIZE);

  return {
    thumbDataUrl,
    hash: computeDHashFromGray(gray, THUMB_SIZE, THUMB_SIZE),
    sharpness: computeSharpnessFromGray(gray, THUMB_SIZE, THUMB_SIZE),
    exposure: computeExposureFromGray(gray),
  };
}

/**
 * Reads EXIF capture time/GPS and derives a duplicate-detection hash plus
 * cheap quality signals from a single canvas decode — everything Smart Bulk
 * Import needs to cluster, cull, and rank photos without any ML model.
 *
 * EXIF reading and canvas decoding are deliberately independent: exifr reads
 * a file's EXIF/HEIC boxes directly from the bytes and doesn't need the
 * browser to be able to *display* the image, but `<img>`/canvas decoding
 * does — and most browsers other than Safari/iOS cannot decode HEIC at all.
 * If canvas analysis fails, the photo still gets a real capture date/GPS (so
 * clustering stays correct) with neutral quality signals and a placeholder
 * thumbnail, instead of the whole photo silently vanishing from the import.
 */
export async function extractPhotoMetadata(file: File): Promise<PhotoMetadata> {
  const [takenAt, gps] = await Promise.all([readTakenAt(file), readGps(file)]);

  try {
    const img = await loadImage(file);
    const { thumbDataUrl, hash, sharpness, exposure } = analyzeCanvas(img);
    return { takenAt, gps, hash, sharpness, exposure, thumbDataUrl };
  } catch {
    return {
      takenAt,
      gps,
      hash: fallbackHash(file),
      sharpness: 0.5,
      exposure: 0.5,
      thumbDataUrl: UNSUPPORTED_PREVIEW_DATA_URL,
    };
  }
}
